// components/forms/Step1.jsx
import React, { useCallback, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useFormContext } from "../../../../contexts/FormContext"; // Ensure correct import path
import useFormValidation from "../../../../hooks/new/useFormValidation"; // Ensure correct import path
import {
  InputField,
  SelectField,
} from "../../../../components/forms/InputField/InputField"; // Ensure correct import path
import "./SignUp.scss";

const Step1 = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const email = searchParams.get("email");
  const phone = searchParams.get("phone");
  const address = searchParams.get("address");
  const city = searchParams.get("city");
  const state = searchParams.get("state");
  const zipcode = searchParams.get("zipcode");

  const { formData, updateField } = useFormContext(); // Access form data and update function
  const { errors, validateField, clearErrors } = useFormValidation();
  const navigate = useNavigate();
  // Array for grade level options
  useEffect(() => {
    if (email) {
      updateField("email", email);
    }
    if (phone) {
      updateField("phone", phone);
    }
    if (address) {
      updateField("address", address);
    }
    if (city) {
      updateField("city", city);
    }
    if (state) {
      updateField("state", state);
    }
    if (zipcode) {
      updateField("zipcode", zipcode);
    }
  }, [address, city, email, phone, state, updateField, zipcode]);
  const gradeOptions = [
    { value: "MD", label: "Middle" },
    { value: "FM", label: "Freshman" },
    { value: "SM", label: "Sophomore" },
    { value: "JR", label: "Junior" },
    { value: "SR", label: "Senior" },
    { value: "GD", label: "Graduated" },
    { value: "OT", label: "Other" },
  ];

  const ageOptions = [...new Array(100)]
    .filter((_, index) => index >= 13)
    .map((_, index) => ({
      label: index + 13, // Displayed in the dropdown
      value: index + 13, // Value that is submitted
    }));

  // Function to validate all fields and determine if there are errors
  const validateFields = () => {
    const fieldsToValidate = [
      "first_name",
      "last_name",
      "email",
      "phone",
      "address",
      "city",
      "state",
      "zipcode",
      "age",
      "grade",
    ];

    // Use a flag to track if there are any errors
    let hasErrors = false;

    // Validate each field and accumulate errors
    fieldsToValidate.forEach((field) => {
      const fieldError = validateField(field, formData[field]);
      if (fieldError) {
        hasErrors = true; // Mark that there are errors
      }
    });

    // Return whether there are any errors
    return hasErrors;
  };

  // Separate handler for the "Continue" button click
  const handleContinue = useCallback(() => {
    console.log("Form data before validation:", formData);

    // Validate fields and check for errors
    const hasErrors = validateFields();
    console.log("hasErrors", hasErrors);

    if (!hasErrors) {
      clearErrors(); // Clear errors before proceeding
      saveToLocalStorage(); // Save data before navigating
      navigate(
        `/new/student/2${location.search}`,
      );
    } else {
      console.error("Validation errors found:", errors);
    }
  }, [formData, clearErrors, navigate, validateFields]);

  // Save form data to localStorage
  const saveToLocalStorage = () => {
    localStorage.setItem("signupFormData", JSON.stringify(formData));
  };

  // Handler to clear the form and localStorage
  const handleClearForm = () => {
    const initialFormData = {
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      state: "",
      zipcode: "",
      age: "",
      grade: "",
      // Include any other fields you have
    };

    // Reset formData in the context
    Object.keys(initialFormData).forEach((field) =>
      updateField(field, initialFormData[field]),
    );

    // Clear localStorage
    localStorage.removeItem("signupFormData");
  };

  return (
    <div className="signup">
      <div className="signup-container">
        <div className="signup-wrapper">
          <InputField
            label="First Name"
            field="first_name"
            value={formData["first_name"] || ""}
            onChange={(value) => updateField("first_name", value)}
            error={errors["first_name"]}
          />
          <InputField
            label="Last Name"
            field="last_name"
            value={formData["last_name"] || ""}
            onChange={(value) => updateField("last_name", value)}
            error={errors["last_name"]}
          />
        </div>
        <div className="signup-wrapper">
          <InputField
            label="Email"
            field="email"
            type="email"
            value={formData.email || ""}
            onChange={(value) => updateField("email", value)}
            error={errors.email}
          />
        </div>

        <div className="signup-wrapper">
          <InputField
            label="Phone"
            field="phone"
            type="tel"
            value={formData.phone || ""}
            onChange={(value) => updateField("phone", value)}
            error={errors.phone}
          />
        </div>
        <div className="signup-wrapper">
          <InputField
            label="Address"
            field="address"
            value={formData.address || ""}
            onChange={(value) => updateField("address", value)}
            error={errors.address}
          />
        </div>
        <div className="signup-wrapper">
          <InputField
            label="City"
            field="city"
            value={formData.city || ""}
            onChange={(value) => updateField("city", value)}
            error={errors.city}
          />
          <InputField
            label="State"
            field="state"
            value={formData.state || ""}
            onChange={(value) => updateField("state", value)}
            error={errors.state}
          />
        </div>

        <div className="signup-wrapper">
          <SelectField
            label="Age"
            field="age"
            value={formData.age || ""}
            options={ageOptions}
            onChange={(value) => updateField("age", value)}
            error={errors.age}
          />
          <InputField
            label="Zip Code"
            field="zipcode"
            value={formData["zipcode"] || ""}
            onChange={(value) => updateField("zipcode", value)}
            error={errors["zipcode"]}
          />
        </div>
        <div className="signup-wrapper">
          <SelectField
            label="What grade or year are you in, if any?"
            field="grade"
            value={formData.grade || ""}
            options={gradeOptions}
            onChange={(value) => updateField("grade", value)}
            error={errors.grade}
          />
        </div>
      </div>

      <div className="signup-navigation-wrapper">
        <button onClick={handleClearForm} className="signup-navigation__clear">
          Clear Form
        </button>
        <button
          onClick={handleContinue}
          className="signup-navigation__continue"
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default Step1;
